import React from "react";
import AppHeader from "../components/AppHeader";
import { Link } from "react-router-dom";

const sectionStyle = {
  background: "var(--bg-muted)",
  borderRadius: "10px",
  padding: "1rem 1.25rem",
  marginBottom: "1.25rem",
  boxShadow: "inset 0 0 4px rgba(0,0,0,0.08)",
};

const h3Style = {
  color: "var(--accent-blue-dark)",
  borderBottom: "1px solid var(--border)",
  paddingBottom: "0.25rem",
  marginBottom: "0.75rem",
  marginTop: 0,
};

const h4Style = {
  fontWeight: 600,
  marginBottom: "0.25rem",
  marginTop: "0.75rem",
  color: "var(--text-secondary)",
};

export default function UserGuide() {
  return (
    <>
      <AppHeader />

      <div
        style={{
          maxWidth: "800px",
          margin: "2rem auto",
          padding: "0 1rem 2rem",
          lineHeight: "1.7",
        }}
      >
        <h2 className="page-header" style={{ textAlign: "center", marginBottom: "0.5rem" }}>
          📖 User Guide
        </h2>
        <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: "1.5rem" }}>
          A step-by-step walkthrough of every CardStoard feature. See <Link to="/about" style={{ color: "var(--link)" }}>About</Link> for a feature overview.
        </p>

        {/* 1. Getting Started */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>1. Getting Started</h3>

          <p style={{ marginTop: 0, fontWeight: 600, marginBottom: "0.25rem" }}>Try the demo</p>
          <p style={{ marginTop: 0 }}>
            Not ready to sign up? Click <strong>Try a Demo</strong> on the login page to explore a real collection
            in read-only mode — no account required. Cards, Gallery, and Analytics are all accessible.
            A welcome panel on the home screen explains what you're looking at and how to navigate.
          </p>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Create an account</p>
          <ol style={{ marginTop: 0 }}>
            <li>Click <strong>Register</strong> on the login page and fill in your username, email, and password.</li>
            <li>Check your inbox for a verification email and click the link to activate your account.</li>
            <li>Log in with your email address <em>or</em> username and password.</li>
          </ol>

          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Navigation bar</p>
          <ul style={{ marginTop: 0, marginBottom: 0 }}>
            <li><strong>📋 My Cards</strong> — your main collection table</li>
            <li><strong>📈 Analytics</strong> — charts and breakdowns</li>
            <li><strong>⚙️ Admin</strong> — settings, valuation factors, dictionary, import/export</li>
            <li><strong>Your username</strong> — account settings and security</li>
            <li><strong>💬 Chat</strong> (if enabled in Admin) — AI Collection Assistant (Cy); knows about all inventory types</li>
          </ul>
        </div>

        {/* 2. My Cards */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>2. My Cards — Managing Your Collection</h3>

          <p style={{ marginTop: 0 }}>
            My Cards is your primary workspace. Navigate to it using the <strong>📋 My Cards</strong> button in the header.
          </p>

          <p style={h4Style}>Adding a card</p>
          <ol style={{ marginTop: 0 }}>
            <li>Click the <strong>＋</strong> button in the top-right corner of the table header.</li>
            <li>Fill in the fields in the new row that appears at the top of the table: First Name, Last Name, Year, Brand, Card #, Rookie checkbox, Grade, and Book Values.</li>
            <li>Click <strong>✓ Save</strong> to add the card, or <strong>✗ Cancel</strong> to discard.</li>
          </ol>
          <p style={{ fontSize: "0.9rem", color: "var(--text-muted)", marginTop: 0 }}>
            Tip: If Smart Fill is enabled in Admin, the card number and rookie flag will auto-populate as you type the player name.
            Press Tab or Enter on a name field to accept the suggested completion.
          </p>
          <p style={{ fontSize: "0.9rem", color: "var(--text-muted)", marginTop: 0 }}>
            Tip: If Image AI is enabled in Admin, click the <strong>📷 Scan Card</strong> button to photograph or upload a card.
            The AI identifies the player, brand, year, and card number and pre-fills the form for you to review before saving.
          </p>

          <p style={h4Style}>Editing a card</p>
          <ol style={{ marginTop: 0 }}>
            <li>Click the <strong>✏️</strong> icon in a card's action column.</li>
            <li>Edit any fields directly in the table row.</li>
            <li>Click <strong>✓ Save</strong>. If you changed book values, they automatically propagate to all other cards with the same player, brand, year, and card number.</li>
          </ol>

          <p style={h4Style}>Card Detail</p>
          <p style={{ marginTop: 0 }}>
            Click the <strong>ℹ️</strong> icon in the action column (or a card's underlined card number if it has images)
            to open the Card Detail page. See <strong>Section 3</strong> below for a full walkthrough.
          </p>

          <p style={h4Style}>Gallery</p>
          <p style={{ marginTop: 0 }}>
            The Gallery page (<strong>🎴 Gallery</strong> in the nav bar) lets you browse all cards that have photos, one at a time.
          </p>
          <ul style={{ marginTop: 0 }}>
            <li>Use the <strong>◀ / ▶</strong> buttons or <strong>← →</strong> arrow keys to move between cards (cross-fade transition).</li>
            <li><strong>Click</strong> a card to flip it front↔back (3D flip animation). Cards with only one image don't flip.</li>
            <li><strong>Double-click</strong> or press <strong>Enter</strong> to open Card Detail for the current card.</li>
            <li>Press <strong>Space</strong> to flip the current card without clicking.</li>
            <li>Use <strong>−</strong> / <strong>+</strong> buttons to resize the card image (200–600px); size is remembered across sessions.</li>
            <li>Search by player, year, or brand; sort by last name, first name, year, or brand.</li>
            <li>Enable Gallery from <strong>Admin → Settings → Nav Bar</strong>.</li>
          </ul>

          <p style={h4Style}>Adding and editing card photos</p>
          <ul style={{ marginTop: 0 }}>
            <li>While editing a row, click <strong>+ Front</strong> or <strong>+ Back</strong> in the image column to select a photo. The <strong>ImageEditor</strong> opens before uploading — rotate, adjust brightness/contrast/saturation, and optionally crop (click <strong>✂ Crop</strong> to show the crop box). Click <strong>Use Photo</strong> to save.</li>
            <li>To re-edit an existing photo, click the thumbnail in the list to open the image viewer, then click <strong>Edit</strong> in the bottom toolbar. The current photo loads into the ImageEditor — adjust and click <strong>Use Photo</strong> to overwrite it. A <strong>Delete Photo</strong> button at the bottom of the editor removes the image entirely (with confirmation).</li>
            <li>Click the thumbnail at any time (outside edit mode) to view front and back images full-size. Use the <strong>Front / Back</strong> toggle in the viewer to switch sides. A red <strong>✕</strong> appears next to <strong>↑ Front</strong> / <strong>↑ Back</strong> in edit mode to delete an uploaded photo.</li>
          </ul>

          <p style={h4Style}>Duplicating and deleting</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>📋 Duplicate</strong> — creates a copy of the card and inserts it directly below the source row, opened for editing. The table order is frozen until you save or cancel; cancelling removes the copy.</li>
            <li><strong>✕ Delete</strong> — asks for confirmation before permanently removing the card.</li>
          </ul>

          <p style={h4Style}>Book freshness refresh</p>
          <p style={{ marginTop: 0 }}>
            Each row's Book column contains a small <strong>↻</strong> button (visible when book values are entered).
            Click it to mark today as the book-value update date without opening edit mode — useful for confirming
            prices are current after a quick check. The left border freshness indicator updates immediately.
          </p>

          <p style={h4Style}>Pin / bookmark</p>
          <p style={{ marginTop: 0 }}>
            Click the <strong>📌</strong> icon in any row's action column to pin that row. The pinned row is highlighted
            and your selection is stored in your account profile — it follows you across devices and sessions. Saving any card
            automatically pins that row so you don't lose your place. To jump back to your pinned row from anywhere
            in the list, click the <strong>📌</strong> button in the table header — the pinned row scrolls to the
            centre of the view. Click the pin icon again on the same row to unpin.
          </p>

          <p style={h4Style}>Graded Cards (PSA / SGC / BGS)</p>
          <p style={{ marginTop: 0 }}>
            Enable graded card tracking in <strong>Admin → Settings → Features → Graded Cards</strong>. When enabled:
          </p>
          <ul style={{ marginTop: 0 }}>
            <li>In edit mode, the Grade cell shows a <strong>Raw / Graded</strong> toggle. Switch to Graded to select a company (PSA / SGC / BGS) and a 1–10 numeric grade.</li>
            <li>Graded cards display a teal badge (e.g. <strong>PSA 9</strong>) instead of the raw condition grade.</li>
            <li>Book values are not applicable for graded cards — the column shows <strong>—</strong>.</li>
            <li>The value field becomes manually editable for graded cards. Enter the market value directly — CardStoard's valuation model is not applied to graded cards.</li>
            <li>Graded cards with grade ≥ 9 participate in row color coding the same as raw Mint cards.</li>
            <li>Switching a graded card back to raw will recalculate its value automatically using existing book values. A confirmation prompt will appear if a graded value is set.</li>
          </ul>

          <p style={h4Style}>Filtering</p>
          <p style={{ marginTop: 0 }}>
            Click the <strong>🔍</strong> icon next to the <em>Last Name</em>, <em>Year</em>, <em>Brand</em>, or <em>Grade</em>  {/* NOSONAR */}
            column headers to open an inline filter input. Filters combine — you can filter by last name and grade at the same time.
            When Graded Cards is enabled, the Grade filter also shows <strong>All / PSA / SGC / BGS / Raw</strong> buttons to filter by card type.
            Click <strong>✕ Clear filters</strong> in the toolbar to reset all filters at once.
          </p>

          <p style={h4Style}>Sorting</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>Quick sort</strong> — click a column header (Last, Year, Grade, Card Value) to sort by that column. Click again to reverse direction. A ▲ or ▼ indicator shows the active direction.</li>
            <li><strong>Advanced sort</strong> — click <strong>⇅ Sort</strong> in the right toolbar to open the sort modal. Add up to 9 levels, each with its own column and ASC/DESC direction. Check <em>"Set as my default sort order"</em> to save this configuration to your profile — it will apply automatically every time you open My Cards.</li>
            <li>The Sort button turns <span style={{ color: "#1a7a1a", fontWeight: 600 }}>green</span> when a custom sort is active and shows the number of active levels.</li>
          </ul>

          <p style={h4Style}>Pagination</p>
          <p style={{ marginTop: 0 }}>
            My Cards loads a page of cards at a time. The page size is set in <strong>Admin → Settings → General → Cards Per Page</strong> (options: 100 / 250 / 500 / 1000 / All).
            When more cards are available beyond the current page, the toolbar shows <strong>X of Y cards</strong> and a <strong>＋</strong> control — click it to append the next page.
            A <strong>Load More</strong> button also appears at the bottom of the table. When all cards are loaded the toolbar shows only the total count.
          </p>

          <p style={h4Style}>Table Navigation (CD Controls)</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            The action column header contains a compact navigation block:
          </p>
          <ul style={{ marginTop: "0.4rem" }}>
            <li><strong>|◄ / ►|</strong> — jump instantly to the top or bottom of the list.</li>
            <li><strong>▲ / ▼</strong> — jump up or down by the selected jump rate.</li>
            <li><strong>Jump rate selector</strong> — choose 25 / 50 / 100 / 250 rows per jump step.</li>
            <li><strong>📌</strong> — jump to your currently pinned row.</li>
            <li><strong>＋</strong> — open the inline new-card entry row.</li>
          </ul>
          <p style={{ marginTop: "0.4rem", marginBottom: 0 }}>
            The centre toolbar shows the total card count and total collection value for the current filtered view.
          </p>
        </div>

        {/* 3. Card Detail */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>3. Card Detail</h3>

          <p style={{ marginTop: 0 }}>
            The Card Detail page shows a full view of a single card. Open it by clicking the <strong>ℹ️</strong> icon
            in the action column, or by clicking the underlined card number of any card that has images attached.
          </p>

          <p style={h4Style}>What's shown</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>Label ID</strong> — the unique <code>CS-CD-XXXXXX</code> identifier printed on the physical label.</li>
            <li><strong>Last updated</strong> — date and time the card record was last modified.</li>
            <li><strong>Duplicate count</strong> — if you own multiple copies, a link shows the count and filters My Cards to those cards.</li>
            <li><strong>Grade badge</strong> — condition grade with colour coding.</li>
            <li><strong>Calculated value</strong> — computed from your book values and grade factors.</li>
            <li><strong>Value change indicator</strong> — a green ↑ or red ↓ appears if the card's value changed within the past 90 days, with the old and new values shown on hover.</li>
            <li><strong>Book freshness</strong> — a label shows if book values are aging (&gt; 30 days, amber) or stale (&gt; 90 days, red). If book values have <em>never</em> been entered, the label <strong>"Book: never updated"</strong> is clickable — clicking it returns you to My Cards with that card open for editing. A <strong>↻</strong> button next to the freshness label resets the timer to today without leaving the page.</li>
            <li><strong>Card images</strong> — front and back images at full size.</li>
          </ul>

          <p style={h4Style}>Notes</p>
          <p style={{ marginTop: 0 }}>
            Use the <strong>Notes</strong> text area to record anything about the card (provenance, condition details, purchase history, etc.).
            Click <strong>Save Notes</strong> to persist them.
          </p>

          <p style={h4Style}>Actions</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>🖨️ Print Label</strong> — opens a label preview modal; click Print to send to your browser's print dialog.</li>
            <li><strong>⬅ Back to List</strong> — returns to My Cards.</li>
          </ul>

          <p style={h4Style}>Navigating between cards</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            When you open Card Detail from My Cards, <strong>← Previous Card</strong> and <strong>Next Card →</strong> buttons
            appear below the other actions. They step through your collection in the same sort order as the list you came from.
            These buttons are not shown when accessing a card directly by URL.
          </p>
        </div>

        {/* 4. Label Printing */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>4. Label Printing &amp; QR Codes</h3>

          <p style={{ marginTop: 0 }}>
            CardStoard generates Avery 6427 labels (1.75" × 0.75") with a QR code and card details for each card.
            Each label includes a QR code — a public shareable card view accessible via QR scan is <em>coming soon</em>.
          </p>

          <p style={h4Style}>Print a single label</p>
          <ol style={{ marginTop: 0 }}>
            <li>Click the <strong>🖨️</strong> icon in any card's action column.</li>
            <li>A preview modal opens showing the label at 3× scale.</li>
            <li>Click <strong>Print</strong> to open the browser print dialog.</li>
          </ol>

          <p style={h4Style}>Print selected cards</p>
          <ol style={{ marginTop: 0 }}>
            <li>Click <strong>Select to Print</strong> in the left toolbar.</li>
            <li>Check the boxes next to the cards you want to print. Use the header checkbox to select all visible cards.</li>
            <li>Click <strong>🖨️ Print Selected (N)</strong> — the label build page opens and the print dialog launches automatically.</li>
            <li>Click <strong>✕ Cancel</strong> at any time to exit selection mode.</li>
          </ol>

          <p style={h4Style}>Print all labels</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Click <strong>🖨️ Print All</strong> in the right toolbar. Labels for your entire collection are generated
            and the print dialog opens automatically. A <strong>✕ Cancel</strong> button is available while the labels are loading.
          </p>
        </div>

        {/* 5. Scan Page */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>5. Scan Page — AI Card Identification</h3>

          <p style={{ marginTop: 0 }}>
            The Scan page (accessible via the <strong>📷 Scan Card</strong> button on My Cards, or from your browser
            when Image AI is enabled in Admin) has two tabs: <strong>Identify Card</strong> and <strong>Scan QR Code</strong>.
          </p>

          <p style={h4Style}>Tab 1 — Identify Card</p>
          <ol style={{ marginTop: 0 }}>
            <li>
              <strong>Choose a photo</strong> — tap the upload area to select an image from your device,
              or click <strong>📷 Use Camera</strong> to capture a photo directly in the browser (front-facing or rear camera).
              Either way, the <strong>ImageEditor</strong> opens so you can crop, rotate, and adjust the photo before identifying.
              For best identification accuracy, import a full-resolution JPG from your camera rather than using live capture —
              the in-app camera is limited to your device's video stream resolution (see resolution note in Batch Capture section).
            </li>
            <li>
              Optionally check <strong>Include condition assessment</strong> to have the AI estimate the card's grade.
            </li>
            <li>Click <strong>🔍 Identify</strong>. The AI analyses the image and returns extracted fields.</li>
          </ol>

          <p style={h4Style}>Results panel</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>Confidence bar</strong> — green (&gt;75%), amber (50–75%), or red (&lt;50%) indicates how certain the AI is about its extraction.</li>
            <li><strong>Extracted Fields</strong> — editable inputs for First Name, Last Name, Year, Brand, Card #, and Grade. Correct any field that the AI got wrong.</li>
            <li>
              <strong>Dictionary Match — Book Values</strong> — if a dictionary entry is found for the identified player,
              brand, year, and card number, the matching book value tiers are shown (H / HM / M / LM / L).
              <br />
              <em>Live re-query:</em> As you edit any field, the dictionary is re-queried automatically after a short pause.
              If the AI mis-read the name, correct it and the correct book values will appear immediately — no need to re-scan.
              The card number is also auto-filled from the dictionary if the AI did not detect one.
            </li>
            <li><strong>Condition Estimate</strong> — if condition assessment was requested, the AI's grade suggestion and condition notes are shown. The Grade dropdown is pre-filled with the suggested grade.</li>
            <li><strong>Already in Collection</strong> — a warning appears if you already own one or more copies of the identified card, with links to the existing cards.</li>
          </ul>

          <p style={h4Style}>Adding to your collection</p>
          <ol style={{ marginTop: 0 }}>
            <li>Review and correct any extracted fields.</li>
            <li>Optionally check <strong>Save this photo as the card's front image</strong>.</li>
            <li>Click <strong>＋ Add to Collection</strong>. The card is created with the current field values and book values from the live dictionary match.</li>
            <li>After adding, click <strong>View in My Cards</strong> to jump to the new card, or <strong>Scan Another</strong> to start over.</li>
          </ol>

          <p style={h4Style}>Tab 2 — Scan QR Code</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Point your device camera at a CardStoard QR label to navigate directly to that card, ball, pack, or set.
            If camera access is unavailable, paste a URL or path (e.g. <code>/card-detail/42</code>) into the
            manual input and click <strong>Go →</strong>.
          </p>
        </div>

        {/* 6. Batch Capture */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>6. Batch Capture — Bulk Photo Session</h3>

          <p style={{ marginTop: 0 }}>
            Batch Capture (📸 in the nav bar) lets you step through your entire collection and photograph
            each card front and back in one session. It's designed for bulk photo days — set up the queue,
            shoot each card, and let the app handle saving the images as you go.
          </p>

          <p style={h4Style}>Session setup</p>
          <ol style={{ marginTop: 0 }}>
            <li>Choose a <strong>Filter</strong> — "Missing photos" (only cards without a front or back image) or "All cards".</li>
            <li>Choose a <strong>Sort order</strong> — Player name, Year, Brand, or Card #. Match your physical stack order.</li>
            <li>The card count and a <strong>First 5 in queue</strong> preview update live as you change filter/sort — use this to confirm the order matches your physical stack before starting.</li>
            <li>Click <strong>Start Session →</strong> to build the queue.</li>
          </ol>

          <p style={h4Style}>Queue review</p>
          <ul style={{ marginTop: 0 }}>
            <li>Review the full ordered list before shooting. Use <strong>▲ / ▼</strong> to reorder cards to match your physical stack.</li>
            <li>Cards with identical player/brand/year/card# are flagged with a <strong>⚠</strong> badge. The warning banner lists each duplicate group and their queue positions — reorder them to be adjacent so you can shoot them together.</li>
            <li>Click <strong>Begin Capture →</strong> when ready.</li>
          </ul>

          <p style={h4Style}>Capturing photos</p>
          <ul style={{ marginTop: 0 }}>
            <li>Each card shows Front and Back capture areas. Click <strong>📷 Camera</strong> to use your device camera, or <strong>📂 File</strong> to upload from your device.</li>
            <li>After selecting or snapping a photo, the <strong>ImageEditor</strong> opens. Use <strong>↺ CCW</strong> / <strong>CW ↻</strong> to rotate, <strong>↔ Landscape</strong> / <strong>↕ Portrait</strong> to toggle aspect ratio for horizontal cards, and the <strong>Bright / Contrast / Sat</strong> sliders for lighting corrections. Click <strong>✂ Crop</strong> to show the crop box and drag to frame the card (hidden by default). Click <strong>Save Default</strong> to persist the current slider values and crop box position — they'll restore automatically next time. <strong>Reset</strong> undoes all changes and clears saved defaults. Click <strong>Use Photo</strong> to save at full resolution.</li>
            <li>
              <strong>Camera vs. file import — resolution note:</strong> The in-app camera captures from your
              device's live video stream (typically 1080p or lower), which is the ceiling the OS/driver exposes
              to the browser regardless of the camera's native sensor resolution. A dedicated camera like a
              DSLR or mirrorless connected via USB will produce a lower-resolution in-app snap than a native
              JPG imported from the same camera's SD card. For archival-quality images, use <strong>📂 File</strong>  {/* NOSONAR */}
              to import the full-resolution JPG directly from the camera. Use <strong>📷 Camera</strong> when
              convenience matters more than maximum resolution.
            </li>
            <li>Photos upload immediately — no separate Save step.</li>
            <li>Click <strong>Next Card →</strong> to advance, or <strong>Skip</strong> to move on without uploading.</li>
            <li>Use <strong>⚠ Next Dupe →</strong> to jump directly to the next duplicate card in the queue.</li>
            <li>Click <strong>⏸ Pause &amp; Exit</strong> at any time — your progress is saved. Return to /batch-capture to resume.</li>
          </ul>

          <p style={h4Style}>Duplicate resolution</p>
          <p style={{ marginTop: 0 }}>
            If two or more cards in a duplicate group both have front photos, a <strong>Resolution screen</strong> appears after
            capture completes. Each group is shown side-by-side with thumbnails. Click <strong>⇄ Swap</strong> to exchange
            the front photos between two duplicates (e.g. if you shot the wrong card first). Click{" "}
            <strong>Confirm Assignments →</strong> when done.
          </p>

          <p style={h4Style}>Summary</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            The summary screen shows photographed, skipped, and duplicate group counts.
            Click <strong>View My Cards</strong> to go back to your collection, or <strong>Start New Session</strong> to run another pass.
          </p>
        </div>

        {/* 7. Valuation Engine */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>7. Valuation Engine</h3>

          <p style={{ marginTop: 0 }}>
            CardStoard uses a proprietary formula to calculate a conservative and accurate estimated value for each
            raw card in your collection. The engine combines publicly available pricing information you enter with
            CardStoard's valuation model — accounting for card condition, rookie status, and other attributes.
            Values are computed automatically each time you save a card.
          </p>

          <p style={h4Style}>Book values</p>
          <p style={{ marginTop: 0 }}>
            Enter up to five price points for each card: <strong>Hi</strong>, <strong>Hi-Mid</strong>, <strong>Mid</strong>, <strong>Lo-Mid</strong>, <strong>Lo</strong>.
            These reflect the publicly available price range for the card (e.g. from a price guide).
            Updating book values on one card automatically propagates to all cards with the same player, brand, year, and card number.
          </p>

          <p style={h4Style}>Grade conditions</p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem", marginTop: 0 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                <th style={{ textAlign: "left", padding: "4px 8px" }}>Grade</th>
                <th style={{ textAlign: "left", padding: "4px 8px" }}>Condition</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["3.0", "NM-MT+ — Near Mint / Mint Plus"],
                ["1.5", "NM — Near Mint"],
                ["1.0", "EX — Excellent"],
                ["0.8", "VG — Very Good"],
                ["0.4", "FR — Fair"],
                ["0.2", "PR — Poor"],
              ].map(([g, label]) => (
                <tr key={g} style={{ borderBottom: "1px solid var(--border-light)" }}>
                  <td style={{ padding: "4px 8px", fontWeight: 600 }}>{g}</td>
                  <td style={{ padding: "4px 8px" }}>{label}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p style={{ marginTop: "0.75rem", marginBottom: 0 }}>
            Click <strong>Revalue All</strong> in Admin → Valuation to reapply the current model to every card
            in your collection. For graded cards (PSA/SGC/BGS), values are entered manually — CardStoard's valuation model is not applied.
          </p>
        </div>

        {/* 9. Player Dictionary & Smart Fill */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>8. Player Dictionary &amp; Smart Fill</h3>

          <p style={{ marginTop: 0 }}>
            The <Link to="/dictionary" style={{ color: "var(--link)" }}>Player Dictionary</Link> is a reference
            database of player names, brands, years, and card numbers. It is seeded with 28,800+ entries covering
            Topps 1952–1990 (every player), Bowman 1948–1955, Fleer 1959–1963, Donruss 1981–1990, and Upper Deck 1989.
            Dictionary entries that match cards in your collection are highlighted in green.
          </p>

          <p style={h4Style}>Smart Fill</p>
          <ol style={{ marginTop: 0 }}>
            <li>Enable Smart Fill in <Link to="/admin" style={{ color: "var(--link)" }}>Admin → Features</Link>.</li>
            <li>When adding a card, type the player's first and last name, then select brand and year.</li>
            <li>Smart Fill automatically fills in the card number, rookie flag, and all 5 book value tiers from the dictionary.</li>
            <li>On name fields, press <strong>Tab</strong> or <strong>Enter</strong> to accept a suggested name completion.</li>
          </ol>

          <p style={h4Style}>Value Dictionary</p>
          <p style={{ marginTop: 0 }}>
            The Value Dictionary stores book values (High → Low) for dictionary entries keyed on brand + year + card number.
            When Smart Fill fires, it first resolves the card number, then immediately looks up book values and populates all 5 tiers automatically.
          </p>
          <ul style={{ marginTop: 0 }}>
            <li>Admin can seed book values from existing cards via <Link to="/admin" style={{ color: "var(--link)" }}>Admin → Value Dictionary → Seed from My Cards</Link>.</li>
            <li>Bulk import book values via CSV from Admin → Value Dictionary → Import Values CSV. Format: Brand, Year, CardNumber, BookHigh, BookHighMid, BookMid, BookLowMid, BookLow. All 5 tiers required per row.</li>
            <li>The Dictionary list shows a <strong>✓</strong> in the Values column for entries that have book values on file.</li>
          </ul>

          <p style={h4Style}>Managing dictionary entries</p>
          <ul style={{ marginTop: 0, marginBottom: 0 }}>
            <li>Browse, filter, and sort entries at <Link to="/dictionary" style={{ color: "var(--link)" }}>/dictionary</Link>.</li>
            <li>Click <strong>✏️</strong> to edit an entry inline, or <strong>＋</strong> in the header to add a new one.</li>
            <li>Updating a player's rookie year prompts you to apply the change across all entries for that player.</li>
            <li>Bulk import entries via CSV from Admin → Dictionary → Import CSV.</li>
          </ul>
        </div>

        {/* 10. Sets */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>9. Sets</h3>

          <p style={{ marginTop: 0 }}>
            The <Link to="/sets" style={{ color: "var(--link)" }}>Sets</Link> page lets you browse global set
            checklists and track which cards you own in each set. CardStoard ships with 39 Topps sets
            (1952–1990) covering 17,504 cards.
          </p>

          <p style={h4Style}>Browsing sets</p>
          <ul style={{ marginTop: 0 }}>
            <li>The set list shows each set's name, brand, year, total entries, and how many you own.</li>
            <li>Click a set row to open the full checklist.</li>
            <li>Use the column 🔍 icons to filter by year, brand, or name.</li>
          </ul>

          <p style={h4Style}>Tracking your build</p>
          <ul style={{ marginTop: 0 }}>
            <li>In a set checklist, click <strong>＋</strong> on any row to add that card to your build.</li>
            <li>Rows in your build are highlighted green. Edit grade, book values, and notes inline.</li>
            <li>Book freshness indicators (amber / red border) appear once book values are entered.</li>
          </ul>

          <p style={h4Style}>Set visibility</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Only sets you've selected appear on your Sets page. Manage visibility in{" "}
            <Link to="/admin" style={{ color: "var(--link)" }}>Admin → Set Visibility</Link> — use the
            brand chip picker to select or deselect entire brands, or toggle individual sets.
          </p>
        </div>

        {/* 11. Sets/Binders */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>10. Sets/Binders</h3>

          <p style={{ marginTop: 0 }}>
            The <Link to="/boxes" style={{ color: "var(--link)" }}>Sets/Binders</Link> page tracks complete
            sets as standalone inventory items — factory-sealed boxes, hand-collated sets, or binder-organised sets.
          </p>

          <p style={h4Style}>Item types</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>Factory</strong> (blue badge) — factory-sealed complete set box</li>
            <li><strong>Collated</strong> (amber badge) — hand-assembled complete set</li>
            <li><strong>Binder</strong> (green badge) — binder-organised set</li>
          </ul>

          <p style={h4Style}>Adding &amp; editing</p>
          <ul style={{ marginTop: 0 }}>
            <li>Click <strong>＋</strong> in the header to add a new item (brand, year, name, type, quantity, value, notes).</li>
            <li>Value is entered directly — no grade-based calculation is applied. Total = quantity × value.</li>
            <li>Click any row to edit inline. Click ✓ to save or ✕ to cancel.</li>
          </ul>

          <p style={h4Style}>Row actions</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>📋</strong> — duplicate the row and open for editing.</li>
            <li><strong>ℹ️</strong> — open the Set Detail page for the full view.</li>
            <li><strong>🖨️</strong> — open the label preview modal; click Print to print an Avery 6427 label with QR code.</li>
          </ul>

          <p style={h4Style}>Set Detail page</p>
          <p style={{ marginTop: 0 }}>
            The Set Detail page shows the <strong>CS-ST-XXXXXX</strong> identifier, type badge, stats (qty / value / total / added),
            and an editable notes field. Use <strong>← Previous Set</strong> / <strong>Next Set →</strong> to step
            through items in the same sort order as the list. The <strong>🖨️ Print Label</strong> button generates
            an Avery 6427 label with QR code; notes appear as a 4th line when present. Scanning the QR code opens
            a public view of the set — no login required.
          </p>

          <p style={h4Style}>Navigation &amp; sort</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Use the CD player controls in the header (|◄ ▲ ▼ ►|) to jump to the top, bottom, or step
            through items. Click <strong>Sort</strong> in the toolbar to configure a multi-level sort order
            and optionally save it as your default.
          </p>
        </div>

        {/* 12. Auto Balls */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>11. Auto Balls</h3>

          <p style={{ marginTop: 0 }}>
            The <Link to="/balls" style={{ color: "var(--link)" }}>Auto Balls</Link> page tracks autographed
            baseballs as a first-class collection type. Each ball records the signer's name, brand, commissioner
            stamp, Certificate of Authenticity (COA) status, inscription, value, and notes.
          </p>

          <p style={h4Style}>Adding &amp; editing</p>
          <ul style={{ marginTop: 0 }}>
            <li>Click <strong>＋</strong> in the header to add a new ball inline (first name, last name required).</li>
            <li>Click <strong>✏️</strong> or any row to edit inline. Click ✓ to save or ✕ to cancel.</li>
            <li>The <strong>COA</strong> checkbox sets the auth flag — the row badge updates to <span style={{ background: "#16a34a", color: "#fff", borderRadius: 3, padding: "1px 6px", fontSize: "0.8rem" }}>AUTH</span> or <span style={{ background: "#9ca3af", color: "#fff", borderRadius: 3, padding: "1px 6px", fontSize: "0.8rem" }}>UNAUTH</span>.</li>
          </ul>

          <p style={h4Style}>Value freshness</p>
          <p style={{ marginTop: 0 }}>
            The Value cell has a left-border freshness indicator — green (&lt; 30 days), amber (30–90 days), or red (&gt; 90 days / never set).
            Click the <strong>↻</strong> button next to any value to confirm it is current and reset the freshness timer to today.
          </p>

          <p style={h4Style}>Row actions</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>📌</strong> — pin the row; pin persists across sessions in your account profile.</li>
            <li><strong>📋</strong> — duplicate the row and open for editing.</li>
            <li><strong>🖨️</strong> — open the label preview and print an Avery 6427 label (CS-BL-XXXXXX + name + inscription + QR code).</li>
            <li><strong>ℹ️</strong> — open the public ball view in a new tab (no login required).</li>
            <li><strong>✕</strong> — delete with confirmation.</li>
          </ul>

          <p style={h4Style}>Navigation &amp; sort</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Use the CD player controls (|◄ ▲ ▼ ►|) to jump through the list. Click <strong>⇅ Sort</strong>  {/* NOSONAR */}
            in the toolbar to configure a multi-level sort order and optionally save it as your default.
            Filter by last name, brand, or auth status using the <strong>🔍</strong> column icons.
          </p>
        </div>

        {/* 13. Wax Boxes */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>12. Wax Boxes</h3>

          <p style={{ marginTop: 0 }}>
            The <Link to="/wax" style={{ color: "var(--link)" }}>Wax Boxes</Link> page tracks sealed wax boxes
            as a first-class inventory type. Each item records the year, brand, box type, quantity, value, and notes.
          </p>

          <p style={h4Style}>Adding &amp; editing</p>
          <ul style={{ marginTop: 0 }}>
            <li>Click <strong>＋</strong> in the header to add a new wax box inline (year and brand required).</li>
            <li>Click <strong>✏️</strong> or any row to edit inline. Click ✓ to save or ✕ to cancel.</li>
            <li>The <strong>Type</strong> dropdown sets the box type — <span style={{ background: "#1d4ed8", color: "#fff", borderRadius: 3, padding: "1px 6px", fontSize: "0.8rem" }}>Cello</span>, <span style={{ background: "#d97706", color: "#fff", borderRadius: 3, padding: "1px 6px", fontSize: "0.8rem" }}>Rack</span>, or <span style={{ background: "#16a34a", color: "#fff", borderRadius: 3, padding: "1px 6px", fontSize: "0.8rem" }}>Std</span>.</li>
          </ul>

          <p style={h4Style}>Value freshness</p>
          <p style={{ marginTop: 0 }}>
            The Value cell has a left-border freshness indicator — green (&lt; 30 days), amber (30–90 days), or red (&gt; 90 days / never set).
            Click the <strong>↻</strong> button to confirm the value is current and reset the freshness timer to today.
          </p>

          <p style={h4Style}>Row actions</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>📌</strong> — pin the row; pin persists across sessions in your account profile.</li>
            <li><strong>📋</strong> — duplicate the row and open for editing.</li>
            <li><strong>🖨️</strong> — open the label preview and print an Avery 6427 label (CS-WX-XXXXXX + year/brand/type + QR code).</li>
            <li><strong>ℹ️</strong> — open the public wax view in a new tab (no login required).</li>
            <li><strong>✕</strong> — delete with confirmation.</li>
          </ul>

          <p style={h4Style}>Navigation &amp; sort</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Use the CD player controls (|◄ ▲ ▼ ►|) to jump through the list. Click <strong>⇅ Sort</strong>  {/* NOSONAR */}
            in the toolbar to configure a multi-level sort order and optionally save it as your default.
            Filter by year, brand, or type using the <strong>🔍</strong> column icons.
          </p>
        </div>

        {/* 14. Wax Packs */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>13. Wax Packs</h3>

          <p style={{ marginTop: 0 }}>
            The <Link to="/packs" style={{ color: "var(--link)" }}>Wax Packs</Link> page tracks individual wax packs
            as a first-class inventory type. Each item records the year, brand, pack type, quantity, value, and notes.
          </p>

          <p style={h4Style}>Adding &amp; editing</p>
          <ul style={{ marginTop: 0 }}>
            <li>Click <strong>＋</strong> in the header to add a new pack inline (year and brand required).</li>
            <li>Click <strong>✏️</strong> or any row to edit inline. Click ✓ to save or ✕ to cancel.</li>
            <li>The <strong>Type</strong> dropdown sets the pack type — <span style={{ background: "#1d4ed8", color: "#fff", borderRadius: 3, padding: "1px 6px", fontSize: "0.8rem" }}>Cello</span>, <span style={{ background: "#d97706", color: "#fff", borderRadius: 3, padding: "1px 6px", fontSize: "0.8rem" }}>Rack</span>, <span style={{ background: "#16a34a", color: "#fff", borderRadius: 3, padding: "1px 6px", fontSize: "0.8rem" }}>Wax</span>, or <span style={{ background: "#7c3aed", color: "#fff", borderRadius: 3, padding: "1px 6px", fontSize: "0.8rem" }}>Blister</span>.</li>
          </ul>

          <p style={h4Style}>Value freshness</p>
          <p style={{ marginTop: 0 }}>
            The Value cell has a left-border freshness indicator — green (&lt; 30 days), amber (30–90 days), or red (&gt; 90 days / never set).
            Click the <strong>↻</strong> button to confirm the value is current and reset the freshness timer to today.
          </p>

          <p style={h4Style}>Row actions</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>📌</strong> — pin the row; pin persists across sessions in your account profile.</li>
            <li><strong>📋</strong> — duplicate the row and open for editing.</li>
            <li><strong>🖨️</strong> — open the label preview and print an Avery 6427 label (CS-PK-XXXXXX + year/brand/type + QR code).</li>
            <li><strong>ℹ️</strong> — open the public pack view in a new tab (no login required).</li>
            <li><strong>✕</strong> — delete with confirmation.</li>
          </ul>

          <p style={h4Style}>Navigation &amp; sort</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Use the CD player controls (|◄ ▲ ▼ ►|) to jump through the list. Click <strong>⇅ Sort</strong>  {/* NOSONAR */}
            in the toolbar to configure a multi-level sort order and optionally save it as your default.
            Filter by year, brand, or type using the <strong>🔍</strong> column icons.
          </p>
        </div>

        {/* 15. Analytics */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>14. Analytics</h3>

          <p style={{ marginTop: 0 }}>
            The <Link to="/analytics" style={{ color: "var(--link)" }}>Analytics</Link> dashboard gives you a high-level
            view of your collection's composition and value over time.
          </p>

          <p style={h4Style}>Summary totals</p>
          <p style={{ marginTop: 0 }}>
            The top of the page shows total cards, total collection value, and unique player count.
          </p>

          <p style={h4Style}>Charts</p>
          <p style={{ marginTop: 0 }}>
            Toggle between three views:
          </p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>Inventory</strong> — bar chart of cards added per month</li>
            <li><strong>Valuation</strong> — line chart of total collection value over time</li>
            <li><strong>Combined</strong> — dual Y-axis chart showing both together</li>
          </ul>
          <p style={{ marginTop: 0 }}>Use the <strong>Year</strong> filter to focus on a specific period.</p>

          <p style={h4Style}>Breakdown tables</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            The Brand, Year, and Player tables show count and total value for each group.
            Click any row to navigate to My Cards pre-filtered to that group.
          </p>

          <p style={h4Style}>Generate Report</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Click <strong>📑 Generate Report</strong> at the top of the Analytics page to download a PDF summary
            of your collection. Choose which inventory types to include (cards, balls, wax, packs, sets/binders),
            sort order, and scope (all / graded / raw). The PDF is generated server-side and includes all items
            with no pagination limit.
          </p>
        </div>

        {/* 16. Admin */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>15. Admin Settings</h3>

          <p style={{ marginTop: 0 }}>
            All administrative tools are consolidated on the <Link to="/admin" style={{ color: "var(--link)" }}>Admin</Link> page,
            organised into four tabs: <strong>Settings</strong>, <strong>Valuation</strong>, <strong>Dictionary</strong>, and <strong>Data</strong>.
            Each tab contains collapsible sections — click a section header to expand or collapse it.
            Use the <strong>Expand All / Collapse All</strong> links to open or close all sections at once.
          </p>

          <p style={h4Style}>Feature toggles</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>Smart Fill</strong> — enable/disable dictionary-based auto-population</li>
            <li><strong>Image AI</strong> — enable/disable card image recognition (📷 Scan Card button); requires Anthropic API key</li>
            <li><strong>Collection Assistant</strong> — enable/disable the AI chatbot (Cy); aware of Cards, Balls, Wax Boxes, Wax Packs, and Sets/Binders (requires Anthropic API key)</li>
            <li><strong>Dark Mode</strong> — toggle the dark colour theme; persists across sessions</li>
          </ul>

          <p style={h4Style}>Card brands</p>
          <p style={{ marginTop: 0 }}>
            Manage the list of card brands available in the Brand dropdown when adding/editing cards.
            Type a brand name and press Enter or comma to add it; click the × on a chip to remove it.
          </p>

          <p style={h4Style}>Valuation factors</p>
          <p style={{ marginTop: 0 }}>
            Valuation factors (grade, rookie, era multipliers) are managed by CardStoard and published to all users.
            When updated factors are available, a ⚡ banner appears on your home screen and on this tab — click
            <strong> Apply to My Collection</strong> (home screen) or <strong>Apply Global Valuation</strong> (this tab)
            to recompute every card's value with the latest factors. The date and time of your last valuation is shown  {/* NOSONAR */}
            below the button.
          </p>

          <p style={h4Style}>Book freshness reset</p>
          <p style={{ marginTop: 0 }}>
            Click <strong>⏱️ Reset Book Value Timers</strong> to mark today as the book-value update date for
            every card that has book values entered. Use this after a bulk review session to establish a fresh
            baseline — rows will show as current (no amber or red border) until 30 or 90 days pass.
          </p>

          <p style={h4Style}>Row colours</p>
          <p style={{ marginTop: 0 }}>
            Customise the highlight colours for Mint cards, Rookie cards, and the Rookie + Mint combination
            using the colour pickers. Click <strong>Restore Defaults</strong> to reset to the original colours.
            Default scheme: <strong>gold</strong> = Rookie + Mint (grade 3.0), <strong>blue</strong> = other-grade rookies, <strong>lavender</strong> = Mint only.
          </p>

          <p style={h4Style}>Set visibility</p>
          <p style={{ marginTop: 0 }}>
            Use the brand chip picker to select which sets appear on your Builds page.
            Deselect all chips in a brand to hide that brand's sets; select individual chips to show only specific sets.
          </p>

          <p style={h4Style}>Cards per page</p>
          <p style={{ marginTop: 0 }}>
            Choose how many cards load per page in My Cards: <strong>100 / 250 / 500 / 1000 / All</strong>.
            The default is 250. Choosing <strong>All</strong> loads your entire collection in one request — recommended only for smaller collections.
            For large collections, a lower page size keeps the list responsive; use the Load More button or the <strong>＋</strong> toolbar control to fetch the next page.
          </p>

          <p style={h4Style}>Nav bar customization</p>
          <p style={{ marginTop: 0 }}>
            Use the Nav Items toggles to show or hide individual centre nav buttons (Add Card, My Cards, Analytics).
            Changes take effect immediately and persist across sessions.
          </p>

          <p style={h4Style}>Dictionary tools</p>
          <ul style={{ marginTop: 0 }}>
            <li>View/Edit dictionary, Import CSV, Add Entry — all accessible from the <strong>Dictionary</strong> tab.</li>
            <li><strong>Check Duplicates</strong> — scans for duplicate entries (same player + brand + year + card number). If found, shows a count and offers a one-click remove; the record with the most recent book values is kept.</li>
            <li><strong>Check Invalid Entries</strong> — finds entries with brand/year combinations that couldn't exist historically (e.g. Score 1952, Upper Deck 1975). If found, shows a count by brand and offers a one-click purge.</li>
            <li><strong>Seed from My Cards</strong> — populates book values on dictionary entries using the book values entered on your cards.</li>
            <li><strong>Import Values CSV</strong> — bulk import book values from a CSV file (Brand, Year, CardNumber, BookHigh, BookHighMid, BookMid, BookLowMid, BookLow).</li>
          </ul>

          <p style={h4Style}>Card Import</p>
          <p style={{ marginTop: 0 }}>
            Import cards in bulk from a CSV file. Use the <strong>Validate</strong> button to check your file
            before importing. See the <Link to="/import-help" style={{ color: "var(--link)" }}>Import Help</Link> page
            for the required column format.
          </p>

          <p style={h4Style}>Data Management</p>
          <ul style={{ marginTop: 0, marginBottom: 0 }}>
            <li><strong>Extract</strong> — download your cards as CSV, TSV, or JSON.</li>
            <li><strong>Backup</strong> — download a full backup (cards + settings) as JSON.</li>
            <li><strong>Restore</strong> — upload a backup file to replace your collection. This is irreversible — back up first.</li>
          </ul>
        </div>

        {/* 17. Subscription Tiers */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>16. Subscription Tiers</h3>

          <p style={{ marginTop: 0 }}>
            CardStoard offers four subscription tiers. Your current tier is shown on the Account page and determines which features and inventory types you can access.
          </p>

          <div style={{ overflowX: "auto", marginBottom: "1rem" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)" }}>
                  <th style={{ textAlign: "left", padding: "6px 10px" }}>Feature</th>
                  <th style={{ textAlign: "center", padding: "6px 10px" }}>Base</th>
                  <th style={{ textAlign: "center", padding: "6px 10px" }}>RC</th>
                  <th style={{ textAlign: "center", padding: "6px 10px" }}>SP</th>
                  <th style={{ textAlign: "center", padding: "6px 10px" }}>Elite</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Card Limit",                     "300",  "1,000", "3,000", "Unlimited"],
                  ["My Cards",                       "✅",   "✅",    "✅",    "✅"],
                  ["Gallery",                        "❌",   "✅",    "✅",    "✅"],
                  ["Analytics",                      "❌",   "✅",    "✅",    "✅"],
                  ["Smart Fill",                     "❌",   "✅",    "✅",    "✅"],
                  ["Multi-Inventory (Balls/Wax/Packs/Sets)", "❌", "❌", "✅", "✅"],
                  ["Graded Cards (PSA/SGC/BGS)",     "❌",   "❌",    "✅",    "✅"],
                  ["Set Builder (Builds)",           "❌",   "❌",    "❌",    "✅"],
                  ["Label Printing",                 "❌",   "❌",    "❌",    "✅"],
                  ["Image AI (Scan & Identify)",     "❌",   "❌",    "❌",    "✅"],
                  ["Collection Assistant (Chatbot)", "❌",   "❌",    "❌",    "✅"],
                ].map(([feature, base, rc, sp, elite]) => (
                  <tr key={feature} style={{ borderBottom: "1px solid var(--border-light)" }}>
                    <td style={{ padding: "5px 10px" }}>{feature}</td>
                    <td style={{ textAlign: "center", padding: "5px 10px" }}>{base}</td>
                    <td style={{ textAlign: "center", padding: "5px 10px" }}>{rc}</td>
                    <td style={{ textAlign: "center", padding: "5px 10px" }}>{sp}</td>
                    <td style={{ textAlign: "center", padding: "5px 10px" }}>{elite}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={h4Style}>Tier names</p>
          <ul style={{ marginTop: 0 }}>
            <li><strong>Base</strong> — free tier; card catalog only, ideal for trying out the app.</li>
            <li><strong>RC (Rookie Card)</strong> — light collector; adds Smart Fill, Gallery, and Analytics.</li>
            <li><strong>SP (Short Print)</strong> — active collector; adds multi-inventory types and graded card tracking.</li>
            <li><strong>Elite</strong> — power user and dealer; unlocks everything including Set Builder, label printing, AI tools, and unlimited cards.</li>
          </ul>

          <p style={h4Style}>Upgrading your tier</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Subscription tier is managed by the CardStoard admin. Your current tier is visible on the Account page. Features locked to a higher tier are hidden or greyed out in the interface until your tier is upgraded.
          </p>
        </div>

        {/* 18. Account & Security */}
        <div style={sectionStyle}>
          <h3 style={h3Style}>17. Account &amp; Security</h3>

          <p style={{ marginTop: 0 }}>
            Access your account settings by clicking your username in the top-right of the navigation bar.
          </p>

          <p style={h4Style}>Last login</p>
          <p style={{ marginTop: 0 }}>
            Your most recent login date and time is displayed at the top of the Account page.
          </p>

          <p style={h4Style}>Change username or password</p>
          <ul style={{ marginTop: 0 }}>
            <li>Enter a new username and click <strong>Update Username</strong>.</li>
            <li>To change your password, enter your current password and your new password (twice). Password requirements: 8+ characters, uppercase, lowercase, number, and special character.</li>
          </ul>

          <p style={h4Style}>Multi-factor authentication (TOTP) <em style={{ fontWeight: 400, color: "var(--text-muted)" }}> — coming soon</em></p>
          <p style={{ marginTop: 0 }}>
            TOTP-based two-factor authentication (via apps like Google Authenticator or Authy) is planned for a future release.
          </p>

          <p style={h4Style}>Delete account</p>
          <p style={{ marginTop: 0, marginBottom: 0 }}>
            Clicking <strong>Delete Account</strong> permanently removes your account, all cards, and all settings.
            This action cannot be undone. A confirmation dialog requires you to confirm before proceeding.
          </p>
        </div>

        <p style={{ textAlign: "center", fontSize: "0.9rem", color: "var(--text-faint)", marginTop: "1rem" }}>
          © CardStoard {new Date().getFullYear()} — All rights reserved.
        </p>
      </div>
    </>
  );
}
